<template>
  <extensions-permission-guard :situation-id="situationId">
    <v-container>
      <v-row>
        <v-col cols="12" lg="10" offset-lg="1">
          <v-row class="my-5 ml-1">
            <v-col cols="8">
              <router-link :to="{ name: 'adminExhibitNew' }">
                <v-btn color="success" dark tile> 新規作成 </v-btn>
              </router-link>
              <router-link :to="{ name: 'adminGallerySort' }">
                <v-btn class="ml-3">表示順変更</v-btn>
              </router-link>
            </v-col>
            <v-col cols="3" class="text-right">
              <v-btn
                class="ma-2"
                tile
                outlined
                small
                color="default"
                @click="displayPublishedStatusDialog = true"
              >
                <v-icon left small> mdi-cog </v-icon>
                公開設定
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="mt-10">
            <div
              v-for="(exhibit, index) in exhibits"
              :key="exhibit.id"
              style="margin: 20px 0 20px 20px"
            >
              <v-hover v-slot="{ hover }">
                <ExhibitBlockLayout :src="exhibit.file" :hover="hover">
                  <v-chip
                    v-if="exhibit.published"
                    color="green"
                    style="color: white"
                    class="ma-2"
                    >公開</v-chip
                  >
                  <v-chip v-else color="red" style="color: white" class="ma-2"
                    >非公開</v-chip
                  >
                  <div v-if="hover" class="mt-7">
                    <router-link
                      :to="{
                        name: 'adminExhibitEdit',
                        params: { id: exhibit.id },
                      }"
                    >
                      <v-btn text block style="color: white"> 編集する </v-btn>
                    </router-link>
                    <v-btn
                      class='mt-10'
                      text
                      block
                      style="color: white"
                      @click="deleteExhibit(exhibit, index)"
                      >削除する</v-btn
                    >
                  </div>
                </ExhibitBlockLayout>
              </v-hover>
            </div>
          </v-row>
          <infinite-loading
            spinner="spiral"
            class="mt-2"
            @infinite="infiniteHandler"
          >
            <template v-slot:no-more>
              <v-chip label outlined> 全て表示されました。 </v-chip>
            </template>
            <template v-slot:no-results>
              <v-chip label outlined> 表示できるものがありません。 </v-chip>
            </template>
          </infinite-loading>
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="displayPublishedStatusDialog" max-width="500">
      <v-card>
        <v-card-title> 公開設定 </v-card-title>
        <v-card-text>
          <v-switch
            :input-value="site.isGalleryPublishedOnTopPage"
            :label="labelText"
            hint="※トップページへの公開/非公開を設定します"
            persistent-hint
            @change="changePublishedStatus($event)"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="error" text small @click="displayPublishedStatusDialog = false">
            キャンセル
          </v-btn>
          <v-btn color="green darken-1" text small @click="updatePublishedStatus">
            保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </extensions-permission-guard>
</template>

<script>
import ExtensionsPermissionGuard from '@/components/commons/ExtensionsPermissionGuard'
import ExhibitBlockLayout from '@/components/commons/exhibits/ExhibitBlockLayout'
import axios from 'axios'
import { deserialize } from 'deserialize-json-api'
import InfiniteLoading from 'vue-infinite-loading'
import { EXHIBITS_SITUATION_ID } from '@/mixins/site_permission_utils'
import imageUtilsMixin from '@/mixins/image_utils'
import headUtilsMixin from '@/mixins/head_utils'

export default {
  components: {
    InfiniteLoading,
    ExtensionsPermissionGuard,
    ExhibitBlockLayout
  },
  mixins: [imageUtilsMixin, headUtilsMixin],
  data: function () {
    return {
      pageTitle: 'ギャラリー管理',
      exhibits: [],
      page: 1,
      situationId: EXHIBITS_SITUATION_ID,
      selectablePublishedStatus: [
        { text: '公開', value: true },
        { text: '非公開', value: false }
      ],
      galleryPublishedStatus: null,
      displayPublishedStatusDialog: false
    }
  },
  computed: {
    labelText () {
      if (this.galleryPublishedStatus === null) {
        this.changePublishedStatus(this.site.isGalleryPublishedOnTopPage)
      }
      return this.galleryPublishedStatus ? '公開' : '非公開'
    }
  },
  methods: {
    infiniteHandler ($state) {
      axios
        .get('/api/exhibits', {
          params: {
            page: this.page
          }
        })
        .then(({ data }) => {
          const deserializedData = deserialize(data).data
          if (deserializedData.length > 0) {
            this.page++
            this.exhibits = this.exhibits.concat(deserializedData)
            $state.loaded()
          } else {
            $state.complete()
          }
        })
    },
    deleteExhibit (exhibit, index) {
      if (!confirm('削除してもよろしいですか？')) return

      axios.delete(`/api/exhibits/${exhibit.id}`).then(
        () => {
          this.exhibits.splice(index, 1)
          const payload = {
            status: 200,
            message: '削除しました。'
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        },
        (error) => {
          const payload = {
            status: error.response.status,
            message: error.response.data.message
          }
          this.$store.dispatch('SystemMessageStore/createMessage', payload)
        }
      )
    },
    changePublishedStatus (value) {
      this.galleryPublishedStatus = value
    },
    updatePublishedStatus () {
      this.$store.dispatch('LoaderStore/displayLoader')
      if (this.galleryPublishedStatus === null) {
        this.$store.dispatch('LoaderStore/hideLoader')
        this.$store.dispatch('SystemMessageStore/createMessage', { status: 200, message: '変更を保存しました' })
      } else {
        axios.patch(`/api/sites/${this.site.id}`, { isGalleryPublishedOnTopPage: this.galleryPublishedStatus }).then(
          () => {
            const payload = {
              status: 200,
              message: '変更を保存しました'
            }
            this.$store.dispatch('SiteStore/fetchSiteReference')
            this.$store.dispatch('LoaderStore/hideLoader')
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          },
          (error) => {
            const payload = {
              status: error.response.status,
              message: error.response.data.errors.toString()
            }
            this.$store.dispatch('LoaderStore/hideLoader')
            this.$store.dispatch('SystemMessageStore/createMessage', payload)
          }
        )
      }
      this.displayPublishedStatusDialog = false
    }
  }
}
</script>

<style scoped>
.img-hover-background {
  position: relative;
}
.img-hover-background::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: black;
  opacity: 0.5;
}
</style>
